import { Input, Label } from '@slal/ui/forms';
import { Col, Row } from '@slal/ui/layout';
import { Controller } from '@slal/ui/react-hook-form/core';
import { ShowWhen } from '@slal/ui/react-hook-form/utils';
import { useEffect } from 'react';
import { NumericFormat } from 'react-number-format';
import { Card, useAnalytics } from '~/components';
import { isAllowedRange } from '~/utils';
import { useChargesCardData } from './hooks';
import { chargeTypeOptions, strings } from './strings';
import { FieldContentWrapper, StyledField, StyledInputGroup, StyledSwitch, StyledText } from './styles';

export const InvestmentAmountAndChargesCard = () => {
    const { pushEvent } = useAnalytics();
    const { isCardDisabled, control, watch, setValue } = useChargesCardData();
    const initialAdviserChargeType = watch('initialAdviserCharge_type');
    const ongoingAdviserChargeType = watch('ongoingAdviserCharge_type');

    useEffect(() => {
        if (initialAdviserChargeType === 'percent') {
            setValue('initialAdviserCharge_amount', '');
        } else {
            setValue('initialAdviserCharge_percent', '');
        }
    }, [initialAdviserChargeType]);

    useEffect(() => {
        if (ongoingAdviserChargeType === 'percent') {
            setValue('ongoingAdviserCharge_amount', '');
        } else {
            setValue('ongoingAdviserCharge_percent', '');
        }
    }, [ongoingAdviserChargeType]);

    return (
        <Card heading={strings.investmentAmountAndCharges} disabled={isCardDisabled}>
            <Row>
                <Col>
                    <StyledField label={strings.investmentAmount} labelFor="investmentAmount">
                        <StyledInputGroup>
                            <Label>{strings.currencySymbol}</Label>
                            <Controller
                                name="investmentAmount"
                                control={control}
                                render={({ field }) => (
                                    <NumericFormat
                                        {...field}
                                        id="investmentAmount"
                                        valueIsNumericString
                                        onBlur={e => {
                                            const activeElement = document.activeElement;
                                            if (!activeElement || activeElement === e.target) {
                                                return;
                                            }
                                            pushEvent({
                                                eventAction: 'Investment amount',
                                                eventLabel: 'Option selected- amount',
                                            });
                                        }}
                                        thousandSeparator=","
                                        allowNegative={false}
                                        customInput={Input}
                                        isAllowed={isAllowedRange(1, 99999999)}
                                        autoComplete="off"
                                    />
                                )}
                            />
                        </StyledInputGroup>
                    </StyledField>
                    <StyledField label={strings.slAnnualManagementCharge}>
                        <StyledText>{strings.slAnnualManagementChargeInfo}</StyledText>
                        <StyledText>
                            *Costs available through our SIPP product.{' '}
                            <a
                                onClick={() => {
                                    pushEvent({
                                        eventAction: 'Investment amount and charges- link click',
                                        eventLabel: `View AMSIPP charges guide`,
                                    });
                                }}
                                target="_blank"
                                href="https://library.standardlife.co.uk/slsip20.pdf"
                            >
                                View AMSIPP charges guide
                            </a>
                        </StyledText>
                    </StyledField>
                    <StyledField
                        label={strings.initialAdviserCharge}
                        helpMessage={strings.helpMessageAdviserCharge}
                        labelFor="initialAdviserCharge"
                    >
                        <FieldContentWrapper>
                            <StyledInputGroup>
                                <ShowWhen fieldName="initialAdviserCharge_type" valueEquals="percent">
                                    <Controller
                                        name="initialAdviserCharge_percent"
                                        control={control}
                                        render={({ field }) => (
                                            <NumericFormat
                                                {...field}
                                                name="initialAdviserCharge_percent"
                                                id="initialAdviserCharge"
                                                customInput={Input}
                                                valueIsNumericString
                                                decimalScale={2}
                                                onBlur={e => {
                                                    const activeElement = document.activeElement;
                                                    if (!activeElement || activeElement === e.target) {
                                                        return;
                                                    }
                                                    pushEvent({
                                                        eventAction: 'Initial adviser charge',
                                                        eventLabel: 'Option selected- percentage',
                                                    });
                                                }}
                                                fixedDecimalScale
                                                allowNegative={false}
                                                isAllowed={isAllowedRange(0, 100)}
                                            />
                                        )}
                                    />
                                    <Label htmlFor="initialAdviserCharge_amount">{strings.percentageSymbol}</Label>
                                </ShowWhen>
                                <ShowWhen fieldName="initialAdviserCharge_type" valueEquals="amount">
                                    <Label htmlFor="initialAdviserCharge_amount">{strings.currencySymbol}</Label>
                                    <Controller
                                        name="initialAdviserCharge_amount"
                                        control={control}
                                        render={({ field }) => (
                                            <NumericFormat
                                                {...field}
                                                name="initialAdviserCharge_amount"
                                                id="initialAdviserCharge"
                                                customInput={Input}
                                                onBlur={e => {
                                                    const activeElement = document.activeElement;
                                                    if (!activeElement || activeElement === e.target) {
                                                        return;
                                                    }
                                                    pushEvent({
                                                        eventAction: 'Initial adviser charge',
                                                        eventLabel: 'Option selected- amount',
                                                    });
                                                }}
                                                valueIsNumericString
                                                thousandSeparator
                                                allowNegative={false}
                                            />
                                        )}
                                    />
                                </ShowWhen>
                            </StyledInputGroup>
                            <StyledSwitch fieldName="initialAdviserCharge_type" options={chargeTypeOptions} />
                        </FieldContentWrapper>
                    </StyledField>
                    <StyledField
                        label={strings.ongoingAdviserCharge}
                        helpMessage={strings.helpMessageAdviserCharge}
                        labelFor="ongoingAdviserCharge"
                    >
                        <FieldContentWrapper>
                            <StyledInputGroup>
                                <ShowWhen fieldName="ongoingAdviserCharge_type" valueEquals="percent">
                                    <Controller
                                        name="ongoingAdviserCharge_percent"
                                        control={control}
                                        render={({ field }) => (
                                            <NumericFormat
                                                {...field}
                                                name="ongoingAdviserCharge_percent"
                                                id="ongoingAdviserCharge"
                                                customInput={Input}
                                                valueIsNumericString
                                                onBlur={e => {
                                                    const activeElement = document.activeElement;
                                                    if (!activeElement || activeElement === e.target) {
                                                        return;
                                                    }
                                                    pushEvent({
                                                        eventAction: 'Ongoing adviser charge',
                                                        eventLabel: 'Option selected- percentage',
                                                    });
                                                }}
                                                decimalScale={2}
                                                fixedDecimalScale
                                                allowNegative={false}
                                                isAllowed={isAllowedRange(0, 100)}
                                            />
                                        )}
                                    />
                                    <Label htmlFor="ongoingAdviserCharge_amount">{strings.percentageSymbol}</Label>
                                </ShowWhen>
                                <ShowWhen fieldName="ongoingAdviserCharge_type" valueEquals="amount">
                                    <Label htmlFor="ongoingAdviserCharge_amount">{strings.currencySymbol}</Label>
                                    <Controller
                                        name="ongoingAdviserCharge_amount"
                                        control={control}
                                        render={({ field }) => (
                                            <NumericFormat
                                                {...field}
                                                name="ongoingAdviserCharge_amount"
                                                id="ongoingAdviserCharge"
                                                customInput={Input}
                                                valueIsNumericString
                                                onBlur={e => {
                                                    const activeElement = document.activeElement;
                                                    if (!activeElement || activeElement === e.target) {
                                                        return;
                                                    }
                                                    pushEvent({
                                                        eventAction: 'Ongoing adviser charge',
                                                        eventLabel: 'Option selected- amount',
                                                    });
                                                }}
                                                thousandSeparator
                                                allowNegative={false}
                                            />
                                        )}
                                    />
                                </ShowWhen>
                            </StyledInputGroup>
                            <StyledSwitch fieldName="ongoingAdviserCharge_type" options={chargeTypeOptions} />
                        </FieldContentWrapper>
                    </StyledField>
                </Col>
            </Row>
        </Card>
    );
};
